import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { motion, AnimatePresence } from "framer-motion";
import { initiatePayment, getPaymentStatus } from "../lib/webService/webService.jsx";
import { Seo } from "../components/Seo";
import { generateNoIndexSEO } from "../lib/seo/metaGenerators";
import { ArrowLeft } from "react-bootstrap-icons";

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const STYLES = `
  @keyframes tip-spin   { to { transform: rotate(360deg); } }
  @keyframes tip-dot    { 0%,100% { opacity:1; transform:translate(-50%,-50%) scale(1); }
                          50% { opacity:0.4; transform:translate(-50%,-50%) scale(0.7); } }
`;

// ─── SVG line-art icons ───────────────────────────────────────────────────────

function CheckIcon({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14.25" stroke="#34C759" strokeWidth="1.5" />
      <polyline
        points="9.5,16.5 13.8,20.8 22.5,11"
        stroke="#34C759" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function ErrorIcon({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="14.25" stroke="#FF3B30" strokeWidth="1.5" />
      <line x1="16" y1="9.5" x2="16" y2="18"
        stroke="#FF3B30" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="16" cy="22.5" r="1.3" fill="#FF3B30" />
    </svg>
  );
}

// ─── Atoms ────────────────────────────────────────────────────────────────────

function SpinRing() {
  return (
    <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto" }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        border: "1.5px solid rgba(0,122,255,0.12)",
      }} />
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        border: "1.5px solid transparent",
        borderTopColor: "#007AFF",
        animation: "tip-spin 0.85s linear infinite",
      }} />
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        width: 8, height: 8, borderRadius: "50%",
        backgroundColor: "#007AFF",
        animation: "tip-dot 1.6s ease-in-out infinite",
      }} />
    </div>
  );
}

function BackButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 36, height: 36, borderRadius: "50%",
        border: "none", background: "rgba(60,60,67,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        flexShrink: 0,
        transition: "opacity 0.15s ease",
        marginBottom: 12,
      }}
    >
      <ArrowLeft size={18} color="#1C1C1E" />
    </button>
  );
}

function IconBadge({ bg, children }) {
  return (
    <div style={{
      width: 72, height: 72, borderRadius: "50%",
      background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto",
    }}>
      {children}
    </div>
  );
}

function PrimaryButton({ onClick, color, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", padding: "14px",
        borderRadius: 14, border: "none",
        background: color, color: "#fff",
        fontSize: "0.9375rem", fontWeight: 600,
        letterSpacing: "-0.01em", cursor: "pointer",
        fontFamily: FONT, transition: "opacity 0.15s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
    >
      {children}
    </button>
  );
}

// ─── State panels ─────────────────────────────────────────────────────────────

const panel = {
  hidden:  { opacity: 0, y: 10, scale: 0.97 },
  visible: { opacity: 1, y: 0,  scale: 1,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit:    { opacity: 0, y: -8, scale: 0.97,
    transition: { duration: 0.2 } },
};

const popIn = {
  initial:  { scale: 0.55, opacity: 0 },
  animate:  { scale: 1, opacity: 1,
    transition: { type: "spring", stiffness: 290, damping: 18, delay: 0.12 } },
};

function ProcessingPanel() {
  return (
    <motion.div key="processing" variants={panel} initial="hidden" animate="visible" exit="exit">
      <SpinRing />

      <p style={{
        margin: "26px 0 6px",
        fontSize: "1.125rem", fontWeight: 700,
        color: "#1C1C1E", letterSpacing: "-0.022em",
      }}>
        Processing Payment
      </p>
      <p style={{
        margin: "0 0 26px",
        fontSize: "0.875rem", color: "rgba(60,60,67,0.55)",
        lineHeight: 1.55,
      }}>
        Please wait while we complete your transaction
      </p>

      {/* Progress bar */}
      <div style={{
        height: 3, borderRadius: 999,
        background: "rgba(0,122,255,0.1)",
        overflow: "hidden", marginBottom: 20,
      }}>
        <motion.div
          style={{ height: "100%", background: "#007AFF", borderRadius: 999 }}
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 1.4, ease: "easeInOut", repeat: Infinity }}
        />
      </div>

      <p style={{
        margin: 0,
        fontSize: "0.73rem", color: "rgba(60,60,67,0.32)",
        letterSpacing: "-0.005em",
      }}>
        Do not refresh or close this page
      </p>
    </motion.div>
  );
}

function SuccessPanel({ onContinue }) {
  return (
    <motion.div key="success" variants={panel} initial="hidden" animate="visible" exit="exit">
      <motion.div {...popIn}>
        <IconBadge bg="rgba(52,199,89,0.1)">
          <CheckIcon size={32} />
        </IconBadge>
      </motion.div>

      <p style={{
        margin: "22px 0 6px",
        fontSize: "1.125rem", fontWeight: 700,
        color: "#1C1C1E", letterSpacing: "-0.022em",
      }}>
        Payment Successful
      </p>
      <p style={{
        margin: "0 0 28px",
        fontSize: "0.875rem", color: "rgba(60,60,67,0.55)",
        lineHeight: 1.55,
      }}>
        Your order has been confirmed and is being prepared
      </p>

      <PrimaryButton onClick={onContinue} color="#34C759">
        Continue Shopping
      </PrimaryButton>
    </motion.div>
  );
}

function FailedPanel({ message, onRetry }) {
  return (
    <motion.div key="failed" variants={panel} initial="hidden" animate="visible" exit="exit">
      <motion.div {...popIn}>
        <IconBadge bg="rgba(255,59,48,0.08)">
          <ErrorIcon size={32} />
        </IconBadge>
      </motion.div>

      <p style={{
        margin: "22px 0 6px",
        fontSize: "1.125rem", fontWeight: 700,
        color: "#1C1C1E", letterSpacing: "-0.022em",
      }}>
        Payment Failed
      </p>
      <p style={{
        margin: "0 auto 28px",
        maxWidth: 280,
        fontSize: "0.875rem", color: "rgba(60,60,67,0.55)",
        lineHeight: 1.55,
      }}>
        {message || "Something went wrong. Please try again."}
      </p>

      <PrimaryButton onClick={onRetry} color="#FF3B30">
        Try Again
      </PrimaryButton>
    </motion.div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

// mzito-pay returns PENDING immediately then polls IntaSend in the background
// (first check at 15s, then exponential backoff). We poll /status/:invoiceId
// until we get a terminal state: COMPLETED, FAILED, or EXPIRED.
const INITIAL_POLL_DELAY_MS = 12000; // just before the backend's first check
const POLL_INTERVAL_MS      = 10000;
const MAX_POLL_DURATION_MS  = 3 * 60 * 1000; // 3 minutes

const TransactionInProgress = () => {
  const { clearCart } = useShoppingCart();
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus]       = useState("PROCESSING");
  const [error, setError]         = useState({ status: false, message: "" });
  const cancelledRef              = useRef(false);
  const isRunningRef              = useRef(false);

  if (!data?.order || !data?.token) {
    navigate("/checkout", { replace: true });
    return null;
  }

  async function handlePayment() {
    if (isRunningRef.current) return; // block concurrent calls
    isRunningRef.current = true;
    cancelledRef.current = false;
    try {
      setIsLoading(true);
      setStatus("PROCESSING");
      setError({ status: false, message: "" });

      // POST returns { invoice_id, state: "PENDING", amount } immediately
      const initial = await initiatePayment(data.order, data.token, data.email);
      const invoiceId = initial?.invoice_id;
      if (!invoiceId) {
        throw new Error(initial?.failed_reason || initial?.message || "Payment could not be initiated");
      }

      // Poll until the backend resolves the STK push.
      // Transient errors (network, 5xx) are swallowed and retried; only
      // terminal states (FAILED/EXPIRED) or consecutive failures give up.
      const startedAt = Date.now();
      await new Promise((r) => setTimeout(r, INITIAL_POLL_DELAY_MS));

      let consecutiveErrors = 0;
      while (Date.now() - startedAt < MAX_POLL_DURATION_MS) {
        if (cancelledRef.current) return;

        let pollResult = null;
        let pollErr = null;
        try {
          pollResult = await getPaymentStatus(invoiceId, data.token);
          consecutiveErrors = 0;
        } catch (err) {
          pollErr = err;
          consecutiveErrors++;
        }

        if (pollResult) {
          const state = pollResult.state;

          if (state === "COMPLETE") {
            if (!cancelledRef.current) {
              clearCart();
              setStatus("COMPLETE");
              setIsLoading(false);
            }
            return;
          }

          if (state === "FAILED") {
            // Terminal state — exit immediately with the provider's reason
            throw new Error(
              pollResult.failed_reason || "Payment could not be completed. Please try again."
            );
          }
          // PENDING / PROCESSING / RETRY — keep waiting
        } else if (consecutiveErrors >= 5) {
          // Too many back-to-back network/server errors — give up
          throw pollErr;
        }
        // Transient error: absorb and retry on next interval

        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }

      throw new Error("Payment is taking too long. Please check your M-Pesa messages.");
    } catch (err) {
      console.error("Payment error:", err);
      if (!cancelledRef.current) {
        const message =
          err.code === "SERVICE_CLOSED"
            ? err.message || "Teco just closed. Please try again once we reopen."
            : err.message || "An error occurred during payment processing";
        setError({ status: true, message });
        setStatus("FAILED");
      }
    } finally {
      isRunningRef.current = false;
      if (!cancelledRef.current) setIsLoading(false);
    }
  }

  useEffect(() => {
    handlePayment();
    return () => { cancelledRef.current = true; };
  }, []);

  const isProcessing = isLoading || status === "PROCESSING";
  const isComplete   = !isLoading && status === "COMPLETE";
  const isFailed     = !isLoading && !isComplete && !isProcessing;

  return (
    <>
      <Seo {...generateNoIndexSEO("Order in Progress")} />
      <style>{STYLES}</style>

      <div style={{
        minHeight: "100dvh",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px 16px",
        background: "rgba(242,242,247,0.55)",
        fontFamily: FONT,
      }}>
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.48, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ width: "100%", maxWidth: 400 }}
        >
          <BackButton onClick={() => navigate(-1)} disabled={isProcessing} />

          <div style={{
            background: "#FFFFFF",
            borderRadius: 22,
            border: "1px solid rgba(60,60,67,0.07)",
            boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
            padding: "48px 32px 40px",
            textAlign: "center",
          }}>
            <AnimatePresence mode="wait">
              {isProcessing && <ProcessingPanel key="processing" />}
              {isComplete   && <SuccessPanel key="success" onContinue={() => navigate("/")} />}
              {isFailed     && <FailedPanel key="failed" message={error.message} onRetry={handlePayment} />}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default TransactionInProgress;
